import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Camera, Eye, EyeOff } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
interface CameraScreenProps {
  isOpen: boolean;
  roomName: string;
  referencePhotoUrl: string;
  onCapture: (photoUrl: string) => void;
  onCancel: () => void;
}
export function CameraScreen({
  isOpen,
  roomName,
  referencePhotoUrl,
  onCapture,
  onCancel
}: CameraScreenProps) {
  const {
    t
  } = useLanguage();
  const [showReference, setShowReference] = useState(true);
  const [isAligned, setIsAligned] = useState(false);
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      // Simulate alignment detection after 2 seconds
      const timer = setTimeout(() => {
        setIsAligned(true);
      }, 2000);
      return () => {
        clearTimeout(timer);
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen, onCancel]);
  const handleCapture = () => {
    // Simulate photo capture - use reference photo as mock result
    onCapture(referencePhotoUrl);
  };
  const toggleReference = () => {
    setShowReference(!showReference);
  };
  return <AnimatePresence data-id="element-57">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.3
    }} className="fixed inset-0 z-50 bg-gray-900" data-id="element-58">
          {/* Camera Viewfinder Background */}
          <div className="absolute inset-0 bg-gradient-to-b from-gray-800 to-gray-900" data-id="element-59" />

          {/* Frame Border - Red when not aligned, Green when aligned */}
          <motion.div animate={{
        borderColor: isAligned ? '#2E8C7E' : '#EF4444'
      }} transition={{
        duration: 0.5
      }} className="absolute inset-4 border-4 rounded-2xl pointer-events-none z-20" style={{
        borderColor: isAligned ? '#2E8C7E' : '#EF4444'
      }} data-id="element-60" />

          {/* Reference Photo Overlay (toggleable) */}
          <AnimatePresence data-id="element-61">
            {showReference && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 0.4
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} className="absolute inset-0 flex items-center justify-center" data-id="element-62">
                <img src={referencePhotoUrl} alt="Reference" className="w-full h-full object-cover" data-id="element-63" />
              </motion.div>}
          </AnimatePresence>

          {/* Top Bar */}
          <div className="absolute top-0 left-0 right-0 p-6 z-30" data-id="element-64">
            <div className="flex items-start justify-between" data-id="element-65">
              {/* Room Badge */}
              <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.2
          }} className="px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20" data-id="element-66">
                <span className="text-white font-semibold" data-id="element-67">{roomName}</span>
              </motion.div>

              {/* Close Button */}
              <motion.button initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.2
          }} onClick={onCancel} className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center hover:bg-white/20 transition-colors" data-id="element-68">
                <X className="w-6 h-6 text-white" data-id="element-69" />
              </motion.button>
            </div>

            {/* Toggle Reference Button - CENTERED BELOW TOP BAR */}
            <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="flex justify-center mt-4" data-id="element-70">
              <button onClick={toggleReference} className="px-6 py-3 rounded-full bg-black/40 backdrop-blur-md border border-white/10 flex items-center gap-2 hover:bg-black/50 transition-colors" data-id="element-71">
                {showReference ? <EyeOff className="w-5 h-5 text-white" data-id="element-72" /> : <Eye className="w-5 h-5 text-white" data-id="element-73" />}
                <span className="text-white font-medium" data-id="element-74">
                  {showReference ? 'Nascondi riferimento' : 'Mostra riferimento'}
                </span>
              </button>
            </motion.div>
          </div>

          {/* Bottom Section */}
          <div className="absolute bottom-0 left-0 right-0 pb-12 px-6 flex flex-col items-center gap-4 z-30" data-id="element-75">
            {/* Alignment Status Indicator - ABOVE TIP TEXT */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.4
        }} data-id="element-76">
              <div className={`px-4 py-2 rounded-full backdrop-blur-md border ${isAligned ? 'bg-teal-deep/80 border-teal-deep text-white' : 'bg-red-500/80 border-red-500 text-white'}`} data-id="element-77">
                <span className="font-semibold" data-id="element-78">
                  {isAligned ? '✓ Allineato' : 'Non allineato'}
                </span>
              </div>
            </motion.div>

            {/* Tip Text */}
            <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="text-white/70 text-sm text-center max-w-md px-4" data-id="element-79">
              {t.fornitore.camera.tip}
            </motion.p>

            {/* Capture Button */}
            <motion.button initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.3
        }} whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} onClick={handleCapture} className="w-20 h-20 rounded-full bg-white shadow-2xl flex items-center justify-center relative group" data-id="element-80">
              {/* Outer ring animation - only when aligned */}
              {isAligned && <div className="absolute inset-0 rounded-full bg-teal-deep/30 animate-ping" data-id="element-81" />}

              {/* Inner circle */}
              <div className="w-16 h-16 rounded-full bg-white border-4 border-gray-900 flex items-center justify-center" data-id="element-82">
                <Camera className="w-8 h-8 text-gray-900" data-id="element-83" />
              </div>
            </motion.button>
          </div>

          {/* Subtle grid overlay for alignment help */}
          <div className="absolute inset-0 pointer-events-none opacity-20 z-10" data-id="element-84">
            <div className="w-full h-full grid grid-cols-3 grid-rows-3" data-id="element-85">
              {[...Array(9)].map((_, i) => <div key={i} className="border border-white/20" data-id="element-86" />)}
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}